import { productGidsMatch } from '@/lib/shopify/gid';
import { formatPrice, toCents, toDollars } from '@/utils/utilities.mjs';
import {
  applyPercentageDiscount,
  applyFixedAmountDiscount,
  cartLineFullPrice,
  cartSavings as sharedCartSavings,
  cartSubtotal as sharedCartSubtotal,
  cartTotal as sharedCartTotal,
  isCampaignActive,
  variantFullPrice,
} from './sharedStrategy';

// Re-export shared primitives so callers that import from this module
// continue to receive the correct implementations.
export { cartLineFullPrice, variantFullPrice };

export const variantSalePrice = (variant, discountSettings) => {
  const fullPrice = variantFullPrice(variant);

  if (!isCampaignActive(discountSettings)) {
    return fullPrice;
  }

  const group = getDiscountSettingsForVariant(
    discountSettings?.shopify_multiple_discount_codes,
    variant,
  );

  if (!group) {
    return fullPrice;
  }

  switch (group.type) {
    case 'percentage':
      return applyPercentageDiscount(fullPrice, group.number);

    case 'fixed_amount':
      return applyFixedAmountDiscount(fullPrice, group.number);

    default:
      console.error(`Unknown discounting type: ${group.type}`);
      return fullPrice;
  }
};

export const cartLineSalePrice = (cartLine, discountSettings) => {
  const fullPrice = cartLineFullPrice(cartLine);

  if (!isCampaignActive(discountSettings)) {
    return fullPrice;
  }

  const group = getDiscountSettingsForVariant(
    discountSettings?.shopify_multiple_discount_codes,
    cartLine.merchandise,
  );

  if (!group) {
    return fullPrice;
  }

  // Trust Shopify line total when it doesn't match the computed full price.
  // Bundles use Shopify's pre-computed line total; campaign discounts are intentionally
  // NOT applied on top — the bundle price IS the sale price.
  const lineTotal = Number(cartLine.cost.totalAmount.amount);

  if (toCents(lineTotal) !== toCents(fullPrice)) {
    return lineTotal;
  }

  // Compute unit sale price directly from the group already resolved above,
  // avoiding a second getDiscountSettingsForVariant call.
  const unitFullPrice = variantFullPrice(cartLine.merchandise);

  switch (group.type) {
    case 'percentage':
      return toDollars(toCents(applyPercentageDiscount(unitFullPrice, group.number)) * (cartLine.quantity ?? 1));

    case 'fixed_amount':
      return toDollars(toCents(applyFixedAmountDiscount(unitFullPrice, group.number)) * (cartLine.quantity ?? 1));

    default:
      console.error(`Unknown discounting type: ${group.type}`);
      return fullPrice;
  }
};

export const promotionalCopy = (discountSettings, variation, variant = null) => {
  if (!isCampaignActive(discountSettings)) {
    return null;
  }

  const group = getDiscountSettingsForVariant(
    discountSettings?.shopify_multiple_discount_codes,
    variant,
  );

  if (!group) {
    return null;
  }

  const copy = discountSettings?.shopify_multiple_discount_codes?.promotional_copy;

  if (!copy) {
    console.warn(
      'promotionalCopy: shopify_multiple_discount_codes.promotional_copy is not configured in CMS',
    );

    return null;
  }

  return buildPromotionalCopy(group, copy, variation);
};

// Resolves the qualifying group once for both code and message. Each tier
// (Elite/Core/Luxe) has its own custom_overlay_message, e.g. "[number] Off for
// [scope] Members" -> "30% Off for Elite Members". No sitewide fallback here,
// that field belongs to the single-code strategy; a group with no message of
// its own falls through to promotionalCopy instead.
export const overlayPromo = (discountSettings, variant = null) => {
  const NONE = { code: null, message: null };
  if (!isCampaignActive(discountSettings)) return NONE;

  const group = getDiscountSettingsForVariant(
    discountSettings?.shopify_multiple_discount_codes,
    variant,
  );
  if (!group) return NONE;

  const code = group.discount_code ?? null;
  const message = group.custom_overlay_message
    ? group.custom_overlay_message
        .replaceAll('[number]', formatDiscountNumber(group))
        .replaceAll('[scope]', group.title ?? '')
    // Reuses promotionalCopy's own CMS-misconfiguration warning rather than
    // re-deriving the fallback copy here.
    : promotionalCopy(discountSettings, 'compact', variant);

  return { code, message };
};

// Pre-discount cart total (sum of full line prices). Matches Helix: campaign
// discounts are shown separately via cartSavings; the discounted amount is
// cartTotal ("Total Before Tax" in CartOrderTotals). subtotal − savings = total.
export const cartSubtotal = (cart, discountSettings) =>
  sharedCartSubtotal(cart, cartLineFullPrice);

export const cartTotal = (cart, discountSettings) =>
  sharedCartTotal(cart, discountSettings, cartLineSalePrice);

export const cartSavings = (cart, discountSettings) =>
  sharedCartSavings(cart, discountSettings, cartLineFullPrice, cartLineSalePrice);

function sortGroupsByHighestNumber(groups) {
  return [...groups].sort(
    (a, b) => (Number(b.number) || 0) - (Number(a.number) || 0),
  );
}


function getDiscountSettingsForVariant(settings, variant = null) {
  if (!settings?.groups?.length) {
    return null;
  }

  if (!variant) {
    return sortGroupsByHighestNumber(settings.groups)[0];
  }

  const groups = sortGroupsByHighestNumber(settings.groups);

  const matchedGroup = groups.find((group) =>
    group.included?.products?.some((product) =>
      productGidsMatch(product.shopify_product_id, variant.product?.id),
    ),
  );

  if (matchedGroup) {
    if (productIdIsExcludedFromGroup(variant.product?.id, matchedGroup.excluded?.products)) {
      return null;
    }
    return matchedGroup;
  }

  // If the product is not included in any group, check if it's in the catch-all group
  const catchAllGroup = groups.find((group) => group.catch_all);

  if (catchAllGroup) {
    if (productIdIsExcludedFromGroup(variant.product?.id, catchAllGroup.excluded?.products)) {
      return null;
    }
    return catchAllGroup;
  }

  return null;
}

function productIdIsExcludedFromGroup(productId, excludedProducts = []) {
  if (productId == null) {
    return false;
  }

  return excludedProducts.some((product) =>
    productGidsMatch(product.shopify_product_id, productId),
  );
}

function buildPromotionalCopy(group, copy, variation) {
  const template = (() => {
    switch (variation) {
      case 'compact':
        return copy?.compact ?? null;
      case 'extended':
        return copy?.extended ?? null;
      case 'normal':
      default:
        return copy?.normal ?? null;
    }
  })();

  if (!template) {
    return null;
  }

  return template
    .replaceAll('[number]', formatDiscountNumber(group))
    .replaceAll('[code]', group.discount_code ?? '');
}

function formatDiscountNumber(group) {
  if (group.number == null || group.number === '') {
    return '';
  }

  switch (group.type) {
    case 'percentage':
      return `${group.number}%`;
    default:
      return formatPrice(group.number);
  }
}
